package world.phantasmal.psolib.fileFormats.quest

import world.phantasmal.psolib.Episode

private val FRIENDLY_NPC_PROPERTIES = listOf(
    EntityProp(name = "Movement distance", offset = 44, type = EntityPropType.F32),
    EntityProp(name = "Hide register", offset = 52, type = EntityPropType.F32),
    EntityProp(name = "Character ID", offset = 56, type = EntityPropType.F32),
    EntityProp(name = "Script label", offset = 60, type = EntityPropType.F32),
    EntityProp(name = "Movement flag", offset = 64, type = EntityPropType.I32)
)

private val ENEMY_PROPERTIES_6 = listOf(
    EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
    EntityProp(name = "Param 2", offset = 48, type = EntityPropType.F32),
    EntityProp(name = "Param 3", offset = 52, type = EntityPropType.F32),
    EntityProp(name = "Param 4", offset = 56, type = EntityPropType.F32),
    EntityProp(name = "Param 5", offset = 60, type = EntityPropType.F32),
    EntityProp(name = "Param 6", offset = 64, type = EntityPropType.I32),
)

private val ENEMY_PROPERTIES_SUBTYPE = listOf(
    EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
    EntityProp(name = "Param 2", offset = 48, type = EntityPropType.F32),
    EntityProp(name = "Param 3", offset = 52, type = EntityPropType.F32),
    EntityProp(name = "Param 4", offset = 56, type = EntityPropType.F32),
    EntityProp(name = "Param 5", offset = 60, type = EntityPropType.F32),
    EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
)

enum class NpcType(
    override val uniqueName: String,
    override val simpleName: String = uniqueName,
    val ultimateName: String = simpleName,
    val episode: Episode? = null,
    val enemy: Boolean = false,
    val minion: Boolean = false,
    val boss: Boolean = false,
    rareType: (() -> NpcType)? = null,
    /**
     * IDs of the areas this NPC can be found in.
     */
    val areaIds: List<Int>,
    /**
     * Type ID used by the game.
     */
    val typeId: Int? = null,
    /**
     * Skin value used by the game.
     */
    val skin: Int? = null,
    /**
     * Boolean specifying whether an NPC is the regular or special variant.
     * Sometimes signifies a variant (e.g. Barbarous Wolf), sometimes a rare variant (e.g. Pouilly
     * Slime).
     */
    val special: Boolean? = null,
    /**
     * NPC-specific properties.
     */
    override val properties: List<EntityProp> = emptyList(),
) : EntityType {
    //
    // Unknown NPCs
    //

    Unknown(
        uniqueName = "Unknown",
        areaIds = listOf(),
    ),

    //
    // Friendly NPCs
    //

    FemaleBase(
        uniqueName = "Female Base",
        areaIds = listOf(0),
        typeId = 1,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    FemaleChild(
        uniqueName = "Female Child",
        areaIds = listOf(0),
        typeId = 2,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    FemaleDwarf(
        uniqueName = "Female Dwarf",
        areaIds = listOf(0),
        typeId = 3,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    FemaleFat(
        uniqueName = "Female Fat",
        areaIds = listOf(0),
        typeId = 4,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    FemaleMacho(
        uniqueName = "Female Macho",
        areaIds = listOf(0),
        typeId = 5,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    FemaleOld(
        uniqueName = "Female Old",
        areaIds = listOf(0),
        typeId = 6,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    FemaleTall(
        uniqueName = "Female Tall",
        areaIds = listOf(0),
        typeId = 7,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleBase(
        uniqueName = "Male Base",
        areaIds = listOf(0),
        typeId = 8,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleChild(
        uniqueName = "Male Child",
        areaIds = listOf(0),
        typeId = 9,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleDwarf(
        uniqueName = "Male Dwarf",
        areaIds = listOf(0),
        typeId = 10,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleFat(
        uniqueName = "Male Fat",
        areaIds = listOf(0),
        typeId = 11,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleMacho(
        uniqueName = "Male Macho",
        areaIds = listOf(0),
        typeId = 12,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleOld(
        uniqueName = "Male Old",
        areaIds = listOf(0),
        typeId = 13,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    MaleTall(
        uniqueName = "Male Tall",
        areaIds = listOf(0),
        typeId = 14,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    BlueSoldier(
        uniqueName = "Blue Soldier",
        areaIds = listOf(0),
        typeId = 25,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    RedSoldier(
        uniqueName = "Red Soldier",
        areaIds = listOf(0),
        typeId = 26,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Principal(
        uniqueName = "Principal",
        areaIds = listOf(0),
        typeId = 27,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Tekker(
        uniqueName = "Tekker",
        areaIds = listOf(0),
        typeId = 28,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    GuildLady(
        uniqueName = "Guild Lady",
        areaIds = listOf(0),
        typeId = 29,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Scientist(
        uniqueName = "Scientist",
        areaIds = listOf(0),
        typeId = 30,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Nurse(
        uniqueName = "Nurse",
        areaIds = listOf(0),
        typeId = 31,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Irene(
        uniqueName = "Irene",
        areaIds = listOf(0),
        typeId = 32,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcHUmar(
        uniqueName = "NPC HUmar",
        simpleName = "HUmar",
        areaIds = listOf(0),
        typeId = 33,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcHUnewearl(
        uniqueName = "NPC HUnewearl",
        simpleName = "HUnewearl",
        areaIds = listOf(0),
        typeId = 34,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcRAmar(
        uniqueName = "NPC RAmar",
        simpleName = "RAmar",
        areaIds = listOf(0),
        typeId = 36,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcRAcast(
        uniqueName = "NPC RAcast",
        simpleName = "RAcast",
        areaIds = listOf(0),
        typeId = 37,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcRAcaseal(
        uniqueName = "NPC RAcaseal",
        simpleName = "RAcaseal",
        areaIds = listOf(0),
        typeId = 38,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcFOmarl(
        uniqueName = "NPC FOmarl",
        simpleName = "FOmarl",
        areaIds = listOf(0),
        typeId = 39,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcFOnewm(
        uniqueName = "NPC FOnewm",
        simpleName = "FOnewm",
        areaIds = listOf(0),
        typeId = 40,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcFOnewearl(
        uniqueName = "NPC FOnewearl",
        simpleName = "FOnewearl",
        areaIds = listOf(0),
        typeId = 41,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcHUnewearl2(
        uniqueName = "NPC HUnewearl 2",
        simpleName = "HUnewearl",
        areaIds = listOf(0),
        typeId = 43,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcHUcast(
        uniqueName = "NPC HUcast",
        simpleName = "HUcast",
        areaIds = listOf(0),
        typeId = 44,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcRAmar2(
        uniqueName = "NPC RAmar 2",
        simpleName = "RAmar",
        areaIds = listOf(0),
        typeId = 45,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcFOmarl2(
        uniqueName = "NPC FOmarl 2",
        simpleName = "FOmarl",
        areaIds = listOf(0),
        typeId = 48,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcFOnewm2(
        uniqueName = "NPC FOnewm 2",
        simpleName = "FOnewm",
        areaIds = listOf(0),
        typeId = 49,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcFOnewearl2(
        uniqueName = "NPC FOnewearl 2",
        simpleName = "FOnewearl",
        areaIds = listOf(0),
        typeId = 50,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcEnemy(
        uniqueName = "NPC Enemy",
        areaIds = listOf(0),
        typeId = 51,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcLappy(
        uniqueName = "NPC Lappy",
        areaIds = listOf(0),
        typeId = 69,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcMoja(
        uniqueName = "NPC Moja",
        areaIds = listOf(0),
        typeId = 70,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    NpcBringer(
        uniqueName = "NPC Bringer",
        areaIds = listOf(0),
        typeId = 169,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Kenkyu(
        uniqueName = "Lab Scientist (Ep. II)",
        simpleName = "Lab Scientist",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 208,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Soutokufu(
        uniqueName = "Commander (Ep. II)",
        simpleName = "Commander",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 209,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Hosa(
        uniqueName = "Assistant (Ep. II)",
        simpleName = "Assistant",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 210,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    KenkyuFemale(
        uniqueName = "Lab Scientist Female (Ep. II)",
        simpleName = "Lab Scientist Female",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 211,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Hosa2(
        uniqueName = "Assistant 2",
        simpleName = "Assistant",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 240,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    ItemShop(
        uniqueName = "Item Shop",
        areaIds = listOf(0),
        typeId = 241,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    DefaultFomar(
        uniqueName = "Default FO'mar",
        simpleName = "Default FO'mar",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 242,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Karen(
        uniqueName = "Karen",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 243,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Leo(
        uniqueName = "Leo",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 244,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Pagini(
        uniqueName = "Pagini",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 245,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Unknown246(
        uniqueName = "Unknown 246",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 246,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Nol(
        uniqueName = "Nol",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 247,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Elly(
        uniqueName = "Elly",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 248,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Unknown249(
        uniqueName = "Unknown 249",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 249,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    ItemShop2(
        uniqueName = "Item Shop (Ep. II)",
        simpleName = "Item Shop",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 250,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    WeaponShop(
        uniqueName = "Weapon Shop (Ep. II)",
        simpleName = "Weapon Shop",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 251,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    SecurityGuard(
        uniqueName = "Security Guard",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 252,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    HuntersGuild(
        uniqueName = "Hunters Guild (Ep. II)",
        simpleName = "Hunters Guild",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 253,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Nurse2(
        uniqueName = "Nurse (Ep. II)",
        simpleName = "Nurse",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 254,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Unknown255(
        uniqueName = "Unknown 255",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 255,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Momoka(
        uniqueName = "Momoka",
        episode = Episode.II,
        areaIds = listOf(0),
        typeId = 256,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),
    Rupika(
        uniqueName = "Rupika",
        episode = Episode.IV,
        areaIds = listOf(0),
        typeId = 280,
        skin = 0,
        special = false,
        properties = FRIENDLY_NPC_PROPERTIES,
    ),

    //
    // Enemy NPCs
    //

    // Episode I Forest

    Hildebear(
        uniqueName = "Hildebear",
        ultimateName = "Hildelt",
        episode = Episode.I,
        enemy = true,
        rareType = { Hildeblue },
        areaIds = listOf(1, 2),
        typeId = 64,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Hildeblue(
        uniqueName = "Hildeblue",
        ultimateName = "Hildetorr",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 64,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    RagRappy(
        uniqueName = "Rag Rappy",
        ultimateName = "El Rappy",
        episode = Episode.I,
        enemy = true,
        rareType = { AlRappy },
        areaIds = listOf(1, 2),
        typeId = 65,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    AlRappy(
        uniqueName = "Al Rappy",
        ultimateName = "Pal Rappy",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 65,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Monest(
        uniqueName = "Monest",
        ultimateName = "Mothvist",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 66,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "State", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Start number", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Total number", offset = 52, type = EntityPropType.F32)
        ),
    ),
    Mothmant(
        uniqueName = "Mothmant",
        ultimateName = "Mothvert",
        episode = Episode.I,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    SavageWolf(
        uniqueName = "Savage Wolf",
        ultimateName = "Gulgus",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 67,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Group ID", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Leader flag", offset = 48, type = EntityPropType.F32),
        ),
    ),
    BarbarousWolf(
        uniqueName = "Barbarous Wolf",
        ultimateName = "Gulgus-Gue",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 67,
        skin = 0,
        special = true,
        properties = listOf(
            EntityProp(name = "Group ID", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Leader flag", offset = 48, type = EntityPropType.F32),
        ),
    ),
    Booma(
        uniqueName = "Booma",
        ultimateName = "Bartle",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 68,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    Gobooma(
        uniqueName = "Gobooma",
        ultimateName = "Barble",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 68,
        skin = 1,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    Gigobooma(
        uniqueName = "Gigobooma",
        ultimateName = "Tollaw",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 68,
        skin = 2,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    Dragon(
        uniqueName = "Dragon",
        ultimateName = "Sil Dragon",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(11),
        typeId = 192,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode I Caves

    GrassAssassin(
        uniqueName = "Grass Assassin",
        ultimateName = "Crimson Assassin",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 96,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    PoisonLily(
        uniqueName = "Poison Lily",
        ultimateName = "Ob Lily",
        episode = Episode.I,
        enemy = true,
        rareType = { NarLily },
        areaIds = listOf(3, 4, 5),
        typeId = 97,
        skin = 0,
        special = false,
    ),
    NarLily(
        uniqueName = "Nar Lily",
        ultimateName = "Mil Lily",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 97,
        skin = 1,
        special = true,
    ),
    // Omnispawn explicit (Ephinea) pseudo-type: forces normal (Poison) Lily regardless of area.
    // Shared type 97 is area-gated; haven.dll registers 228 to force this side.
    PoisonLilyOmni(
        uniqueName = "Poison Lily (Omnispawn)",
        ultimateName = "Ob Lily (Omnispawn)",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 228,
        skin = 0,
        special = false,
    ),
    NanoDragon(
        uniqueName = "Nano Dragon",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 98,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 5", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Spawn Flag", offset = 64, type = EntityPropType.I32),
        ),
    ),
    EvilShark(
        uniqueName = "Evil Shark",
        ultimateName = "Vulmer",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 99,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    PalShark(
        uniqueName = "Pal Shark",
        ultimateName = "Govulmer",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 99,
        skin = 1,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    GuilShark(
        uniqueName = "Guil Shark",
        ultimateName = "Melqueek",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 99,
        skin = 2,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    PofuillySlime(
        uniqueName = "Pofuilly Slime",
        episode = Episode.I,
        enemy = true,
        rareType = { PouillySlime },
        areaIds = listOf(3, 4, 5),
        typeId = 100,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    PouillySlime(
        uniqueName = "Pouilly Slime",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 100,
        skin = 0,
        special = true,
        properties = ENEMY_PROPERTIES_6,
    ),
    PanArms(
        uniqueName = "Pan Arms",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(3, 4, 5),
        typeId = 101,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Migium(
        uniqueName = "Migium",
        episode = Episode.I,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    Hidoom(
        uniqueName = "Hidoom",
        episode = Episode.I,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    DeRolLe(
        uniqueName = "De Rol Le",
        ultimateName = "Dal Ra Lie",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(12),
        typeId = 193,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode I Mines

    Dubchic(
        uniqueName = "Dubchic",
        ultimateName = "Dubchich",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 128,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Gilchic(
        uniqueName = "Gilchic",
        ultimateName = "Gilchich",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 128,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Garanz(
        uniqueName = "Garanz",
        ultimateName = "Baranz",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 129,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    SinowBeat(
        uniqueName = "Sinow Beat",
        ultimateName = "Sinow Blue",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 130,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Invisible", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 5", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Param 6", offset = 64, type = EntityPropType.I32),
        ),
    ),
    SinowGold(
        uniqueName = "Sinow Gold",
        ultimateName = "Sinow Red",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 130,
        skin = 0,
        special = true,
        properties = listOf(
            EntityProp(name = "Invisible", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 5", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Param 6", offset = 64, type = EntityPropType.I32),
        ),
    ),
    Canadine(
        uniqueName = "Canadine",
        ultimateName = "Canabin",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 131,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Canane(
        uniqueName = "Canane",
        ultimateName = "Canune",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 132,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Dubswitch(
        uniqueName = "Dubswitch",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(6, 7),
        typeId = 133,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    VolOptPart1(
        uniqueName = "Vol Opt (Part 1)",
        simpleName = "Vol Opt",
        ultimateName = "Vol Opt ver.2",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 194,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    VolOptPart1Sub(
        uniqueName = "Vol Opt (Part 1 Sub)",
        simpleName = "Vol Opt",
        ultimateName = "Vol Opt ver.2",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 195,
        skin = 0,
        special = false,
    ),
    VolOptCore(
        uniqueName = "Vol Opt (Core)",
        simpleName = "Vol Opt",
        ultimateName = "Vol Opt ver.2",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 196,
        skin = 0,
        special = false,
    ),
    VolOptPart2(
        uniqueName = "Vol Opt (Part 2)",
        simpleName = "Vol Opt",
        ultimateName = "Vol Opt ver.2",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 197,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    VolOptMonitor(
        uniqueName = "Vol Opt (Monitor)",
        simpleName = "Vol Opt",
        ultimateName = "Vol Opt ver.2",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 198,
        skin = 0,
        special = false,
    ),
    VolOptHiraisin(
        uniqueName = "Vol Opt (Lightning)",
        simpleName = "Vol Opt",
        ultimateName = "Vol Opt ver.2",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 199,
        skin = 0,
        special = false,
    ),

    // Episode I Ruins

    Delsaber(
        uniqueName = "Delsaber",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 160,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Jump distance", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Block HP", offset = 48, type = EntityPropType.F32)
        ),
    ),
    ChaosSorcerer(
        uniqueName = "Chaos Sorcerer",
        ultimateName = "Gran Sorcerer",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 161,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    DarkGunner(
        uniqueName = "Dark Gunner",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 162,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    DarkGunnerCenter(
        uniqueName = "Dark Gunner Center",
        episode = Episode.I,
        enemy = true,
        minion = true,
        areaIds = listOf(8, 9, 10),
        typeId = 163,
        skin = 0,
        special = false,
    ),
    DeathGunner(
        uniqueName = "Death Gunner",
        episode = Episode.I,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    ChaosBringer(
        uniqueName = "Chaos Bringer",
        ultimateName = "Dark Bringer",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 164,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    DarkBelra(
        uniqueName = "Dark Belra",
        ultimateName = "Indi Belra",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 165,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Dimenian(
        uniqueName = "Dimenian",
        ultimateName = "Arlan",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 166,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    LaDimenian(
        uniqueName = "La Dimenian",
        ultimateName = "Merlan",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 166,
        skin = 1,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    SoDimenian(
        uniqueName = "So Dimenian",
        ultimateName = "Del-D",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 166,
        skin = 2,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    Bulclaw(
        uniqueName = "Bulclaw",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 167,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Bulk(
        uniqueName = "Bulk",
        episode = Episode.I,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    Claw(
        uniqueName = "Claw",
        episode = Episode.I,
        enemy = true,
        areaIds = listOf(8, 9, 10),
        typeId = 168,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    DarkFalz(
        uniqueName = "Dark Falz",
        episode = Episode.I,
        enemy = true,
        boss = true,
        areaIds = listOf(14),
        typeId = 200,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode II VR Temple

    Hildebear2(
        uniqueName = "Hildebear (Ep. II)",
        simpleName = "Hildebear",
        ultimateName = "Hildelt",
        episode = Episode.II,
        enemy = true,
        rareType = { Hildeblue2 },
        areaIds = listOf(1, 2),
        typeId = 64,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Hildeblue2(
        uniqueName = "Hildeblue (Ep. II)",
        simpleName = "Hildeblue",
        ultimateName = "Hildetorr",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 64,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    RagRappy2(
        uniqueName = "Rag Rappy (Ep. II)",
        simpleName = "Rag Rappy",
        ultimateName = "El Rappy",
        episode = Episode.II,
        enemy = true,
        rareType = { LoveRappy },
        areaIds = listOf(1, 2),
        typeId = 65,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    LoveRappy(
        uniqueName = "Love Rappy",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 65,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    StRappy(
        uniqueName = "St. Rappy",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    HalloRappy(
        uniqueName = "Hallo Rappy",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    EggRappy(
        uniqueName = "Egg Rappy",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    Monest2(
        uniqueName = "Monest (Ep. II)",
        simpleName = "Monest",
        ultimateName = "Mothvist",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 66,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "State", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Start number", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Total number", offset = 52, type = EntityPropType.F32)
        ),
    ),
    Mothmant2(
        uniqueName = "Mothmant (Ep. II)",
        ultimateName = "Mothvert",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    PoisonLily2(
        uniqueName = "Poison Lily (Ep. II)",
        simpleName = "Poison Lily",
        ultimateName = "Ob Lily",
        episode = Episode.II,
        enemy = true,
        rareType = { NarLily2 },
        areaIds = listOf(1, 2),
        typeId = 97,
        skin = 0,
        special = false,
    ),
    NarLily2(
        uniqueName = "Nar Lily (Ep. II)",
        simpleName = "Nar Lily",
        ultimateName = "Mil Lily",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 97,
        skin = 1,
        special = false,
    ),
    GrassAssassin2(
        uniqueName = "Grass Assassin (Ep. II)",
        simpleName = "Grass Assassin",
        ultimateName = "Crimson Assassin",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 96,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Dimenian2(
        uniqueName = "Dimenian (Ep. II)",
        simpleName = "Dimenian",
        ultimateName = "Arlan",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 166,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    LaDimenian2(
        uniqueName = "La Dimenian (Ep. II)",
        simpleName = "La Dimenian",
        ultimateName = "Merlan",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 166,
        skin = 1,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    SoDimenian2(
        uniqueName = "So Dimenian (Ep. II)",
        simpleName = "So Dimenian",
        ultimateName = "Del-D",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 166,
        skin = 2,
        special = false,
        properties = listOf(
            EntityProp(name = "Param 1", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Idle Distance", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Param 4", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    DarkBelra2(
        uniqueName = "Dark Belra (Ep. II)",
        simpleName = "Dark Belra",
        ultimateName = "Indi Belra",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(1, 2),
        typeId = 165,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    BarbaRay(
        uniqueName = "Barba Ray",
        episode = Episode.II,
        enemy = true,
        boss = true,
        areaIds = listOf(14),
        typeId = 203,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode II VR Spaceship

    SavageWolf2(
        uniqueName = "Savage Wolf (Ep. II)",
        simpleName = "Savage Wolf",
        ultimateName = "Gulgus",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 67,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Group ID", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Leader flag", offset = 48, type = EntityPropType.F32),
        ),
    ),
    BarbarousWolf2(
        uniqueName = "Barbarous Wolf (Ep. II)",
        simpleName = "Barbarous Wolf",
        ultimateName = "Gulgus-Gue",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 67,
        skin = 0,
        special = true,
        properties = listOf(
            EntityProp(name = "Group ID", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Leader flag", offset = 48, type = EntityPropType.F32),
        ),
    ),
    PanArms2(
        uniqueName = "Pan Arms (Ep. II)",
        simpleName = "Pan Arms",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 101,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Migium2(
        uniqueName = "Migium (Ep. II)",
        simpleName = "Migium",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    Hidoom2(
        uniqueName = "Hidoom (Ep. II)",
        simpleName = "Hidoom",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    Dubchic2(
        uniqueName = "Dubchic (Ep. II)",
        simpleName = "Dubchic",
        ultimateName = "Dubchich",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 128,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Gilchic2(
        uniqueName = "Gilchic (Ep. II)",
        simpleName = "Gilchic",
        ultimateName = "Gilchich",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 128,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Garanz2(
        uniqueName = "Garanz (Ep. II)",
        simpleName = "Garanz",
        ultimateName = "Baranz",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 129,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Dubswitch2(
        uniqueName = "Dubswitch (Ep. II)",
        simpleName = "Dubswitch",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 133,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Delsaber2(
        uniqueName = "Delsaber (Ep. II)",
        simpleName = "Delsaber",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 160,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Jump distance", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Block HP", offset = 48, type = EntityPropType.F32)
        ),
    ),
    ChaosSorcerer2(
        uniqueName = "Chaos Sorcerer (Ep. II)",
        simpleName = "Chaos Sorcerer",
        ultimateName = "Gran Sorcerer",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(3, 4),
        typeId = 161,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    GolDragon(
        uniqueName = "Gol Dragon",
        episode = Episode.II,
        enemy = true,
        boss = true,
        areaIds = listOf(15),
        typeId = 204,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode II Central Control Area

    SinowBerill(
        uniqueName = "Sinow Berill",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 212,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    SinowSpigell(
        uniqueName = "Sinow Spigell",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 212,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Merillia(
        uniqueName = "Merillia",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 213,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Meriltas(
        uniqueName = "Meriltas",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 213,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Mericarol(
        uniqueName = "Mericarol",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16, 17),
        typeId = 214,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Merikle(
        uniqueName = "Merikle",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16, 17),
        typeId = 214,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Mericus(
        uniqueName = "Mericus",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16, 17),
        typeId = 214,
        skin = 2,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    UlGibbon(
        uniqueName = "Ul Gibbon",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 215,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Spot appear", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Jump appear", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Back jump", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Run tech", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Back tech", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    ZolGibbon(
        uniqueName = "Zol Gibbon",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 215,
        skin = 1,
        special = false,
        properties = listOf(
            EntityProp(name = "Spot appear", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Jump appear", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Back jump", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Run tech", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Back tech", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 64, type = EntityPropType.I32),
        ),
    ),
    Gibbles(
        uniqueName = "Gibbles",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16, 17),
        typeId = 216,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Gee(
        uniqueName = "Gee",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16),
        typeId = 217,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    GiGue(
        uniqueName = "Gi Gue",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(5, 6, 7, 8, 9, 16, 17),
        typeId = 218,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    IllGill(
        uniqueName = "Ill Gill",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(17),
        typeId = 225,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    DelLily(
        uniqueName = "Del Lily",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(17),
        typeId = 97,
        skin = 0,
        special = false,
    ),
    // Omnispawn explicit (Ephinea) pseudo-type: forces Del Lily regardless of area.
    // Shared type 97 is area-gated; haven.dll registers 229 to force this side.
    DelLilyOmni(
        uniqueName = "Del Lily (Omnispawn)",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(17),
        typeId = 229,
        skin = 0,
        special = false,
    ),
    Epsilon(
        uniqueName = "Epsilon",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(17),
        typeId = 224,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    // Omnispawn explicit (Ephinea) pseudo-type: forces Epsilon regardless of area.
    // Shared type 224 is area/skin-gated; haven.dll registers 227 to force this side.
    EpsilonOmni(
        uniqueName = "Epsilon (Omnispawn)",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(17),
        typeId = 227,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    GalGryphon(
        uniqueName = "Gal Gryphon",
        episode = Episode.II,
        enemy = true,
        boss = true,
        areaIds = listOf(12),
        typeId = 192,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode II Seabed

    Deldepth(
        uniqueName = "Deldepth",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 219,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Delbiter(
        uniqueName = "Delbiter",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11, 17),
        typeId = 220,
        skin = 0,
        special = false,
        properties = listOf(
            EntityProp(name = "Howl percent", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Confuse percent", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Confuse distance", offset = 52, type = EntityPropType.F32),
            EntityProp(name = "Laser percent", offset = 56, type = EntityPropType.F32),
            EntityProp(name = "Charge percent", offset = 60, type = EntityPropType.F32),
            EntityProp(name = "Type", offset = 64, type = EntityPropType.I32)
        ),
    ),
    Dolmolm(
        uniqueName = "Dolmolm",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 221,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Dolmdarl(
        uniqueName = "Dolmdarl",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 221,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Morfos(
        uniqueName = "Morfos",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 222,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Recobox(
        uniqueName = "Recobox",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11, 17),
        typeId = 223,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Recon(
        uniqueName = "Recon",
        episode = Episode.II,
        enemy = true,
        minion = true,
        areaIds = listOf(),
    ),
    SinowZoa(
        uniqueName = "Sinow Zoa",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 224,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    SinowZele(
        uniqueName = "Sinow Zele",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 224,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    // Omnispawn explicit (Ephinea) pseudo-type: forces Sinow Zoa regardless of area.
    // Shared type 224 is area/skin-gated; haven.dll registers 226 to force this side.
    SinowZoaOmni(
        uniqueName = "Sinow Zoa (Omnispawn)",
        episode = Episode.II,
        enemy = true,
        areaIds = listOf(10, 11),
        typeId = 226,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    OlgaFlow(
        uniqueName = "Olga Flow",
        episode = Episode.II,
        enemy = true,
        boss = true,
        areaIds = listOf(13),
        typeId = 202,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),

    // Episode IV

    SandRappy(
        uniqueName = "Sand Rappy",
        episode = Episode.IV,
        enemy = true,
        rareType = { DelRappy },
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 65,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    DelRappy(
        uniqueName = "Del Rappy",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 65,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Astark(
        uniqueName = "Astark",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 272,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    SatelliteLizard(
        uniqueName = "Satellite Lizard",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 273,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Yowie(
        uniqueName = "Yowie",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 273,
        skin = 0,
        special = true,
        properties = ENEMY_PROPERTIES_6,
    ),
    MerissaA(
        uniqueName = "Merissa A",
        episode = Episode.IV,
        enemy = true,
        rareType = { MerissaAA },
        areaIds = listOf(6, 7, 8),
        typeId = 274,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    MerissaAA(
        uniqueName = "Merissa AA",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(6, 7, 8),
        typeId = 274,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Girtablulu(
        uniqueName = "Girtablulu",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(6, 7, 8),
        typeId = 275,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_6,
    ),
    Zu(
        uniqueName = "Zu",
        episode = Episode.IV,
        enemy = true,
        rareType = { Pazuzu },
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 276,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Pazuzu(
        uniqueName = "Pazuzu",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5, 6, 7, 8),
        typeId = 276,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Boota(
        uniqueName = "Boota",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5),
        typeId = 277,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    ZeBoota(
        uniqueName = "Ze Boota",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5),
        typeId = 277,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    BaBoota(
        uniqueName = "Ba Boota",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5),
        typeId = 277,
        skin = 2,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Dorphon(
        uniqueName = "Dorphon",
        episode = Episode.IV,
        enemy = true,
        rareType = { DorphonEclair },
        areaIds = listOf(1, 2, 3, 4, 5),
        typeId = 278,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    DorphonEclair(
        uniqueName = "Dorphon Eclair",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(1, 2, 3, 4, 5),
        typeId = 278,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Goran(
        uniqueName = "Goran",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(6, 7, 8),
        typeId = 279,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    PyroGoran(
        uniqueName = "Pyro Goran",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(6, 7, 8),
        typeId = 279,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    GoranDetonator(
        uniqueName = "Goran Detonator",
        episode = Episode.IV,
        enemy = true,
        areaIds = listOf(6, 7, 8),
        typeId = 279,
        skin = 2,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    SaintMilion(
        uniqueName = "Saint-Milion",
        episode = Episode.IV,
        enemy = true,
        boss = true,
        rareType = { Kondrieu },
        areaIds = listOf(9),
        typeId = 281,
        skin = 0,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Shambertin(
        uniqueName = "Shambertin",
        episode = Episode.IV,
        enemy = true,
        boss = true,
        rareType = { Kondrieu },
        areaIds = listOf(9),
        typeId = 281,
        skin = 1,
        special = false,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    ),
    Kondrieu(
        uniqueName = "Kondrieu",
        episode = Episode.IV,
        enemy = true,
        boss = true,
        areaIds = listOf(9),
        typeId = 281,
        skin = 0,
        special = true,
        properties = ENEMY_PROPERTIES_SUBTYPE,
    );

    /**
     * The type of this NPC's rare variant if it has one.
     */
    val rareType: NpcType? by lazy { rareType?.invoke() }

    companion object {
        /**
         * Use this instead of [values] to avoid unnecessary copying.
         */
        val VALUES: Array<NpcType> = entries.toTypedArray()
    }
}
